import React from 'react';
import {
  Box,
  Typography,
  Link,
  IconButton,
  Tooltip,
  Stack,
  Snackbar,
  Chip,
  useTheme,
  ButtonBase
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupsIcon from '@mui/icons-material/Groups';
import YouTubeIcon from '@mui/icons-material/YouTube'; 
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SecurityIcon from '@mui/icons-material/Security';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CookiePreferencesDialog from './cookies/CookiePreferencesDialog'; 
import packageJson from '../../package.json';
import FavoriteIcon from '@mui/icons-material/Favorite';

const Footer: React.FC = () => {
  const [copied, setCopied] = React.useState(false);
  const [cookiePrefsOpen, setCookiePrefsOpen] = React.useState(false);
  const theme = useTheme();

  // Version info - you can get this from package.json or environment variables
  const appVersion = packageJson.version || '1.1.0';

  const currentUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : '';

  // Landing page URL for sharing
  const landingPageUrl = 'https://www.appconfig.eu';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(landingPageUrl);
      setCopied(true);
    } catch {
      setCopied(true);
    }
  };

  const handleLinkedInShare = () => {
    const u = encodeURIComponent(landingPageUrl);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleFeedback = () => {
    const subject = encodeURIComponent('AppConfig² Feedback');
    const body = encodeURIComponent(`Hi AppConfig² Team,\n\nI have feedback about AppConfig²:\n\nVersion: ${appVersion}\nPage: ${currentUrl}\n\nFeedback:\n`);
    window.open(`mailto:feedback@appconfig.app?subject=${subject}&body=${body}`, '_self');
  };

  const handleSupportRequest = () => {
    const subject = encodeURIComponent('AppConfig² Support Request');
    const body = encodeURIComponent(`Hi AppConfig² Support Team,\n\nI need assistance with AppConfig²:\n\nVersion: ${appVersion}\nPage: ${currentUrl}\nTenant ID: [Your Tenant ID]\nUser: [Your Email]\n\nIssue Description:\n\n\nSteps to Reproduce:\n1. \n2. \n3. \n\nExpected Behavior:\n\n\nActual Behavior:\n\n\nAdditional Context:\n`);
    window.open(`mailto:support@appconfig.app?subject=${subject}&body=${body}`, '_self');
  };

  const legalLinkSx = {
    color: 'text.secondary',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    '&:hover': { color: 'primary.main', textDecoration: 'underline' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2
    }
  } as const;

  return (
    <Box
      component="footer"
      role="contentinfo"
      sx={{
        py: 3,
        mt: 0,
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 2, md: 4 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255, 107, 0, 0.3) 50%, transparent 100%)'
        }
      }}
    >
      {/* Main footer sections */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'flex-start' },
          mb: 1,
          gap: 2
        }}
      >
        {/* LEFT: Follow us and Share */}
        <Box sx={{ 
          minWidth: { md: 280 }, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1,
          alignItems: { xs: 'flex-start', md: 'flex-start' }
        }}>
          {/* Follow us */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Follow us:
            </Typography>
            <Tooltip title="YouTube">
              <IconButton
                size="small"
                component="a"
                href="https://www.youtube.com/@appconfigsquared"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Subscribe on YouTube"
              >
                <YouTubeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="LinkedIn">
              <IconButton
                size="small"
                component="a"
                href="https://www.linkedin.com/company/appconfig-square"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow on LinkedIn"
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="GitHub">
              <IconButton
                size="small"
                component="a"
                href="https://github.com/toninofilip/appconfig-suite#readme"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View on GitHub"
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Share AppConfig² */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Share AppConfig²:
            </Typography>
            <Tooltip title="Share on LinkedIn">
              <IconButton
                size="small"
                onClick={handleLinkedInShare}
                aria-label="Share on LinkedIn"
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy link">
              <IconButton
                size="small"
                onClick={handleCopyLink}
                aria-label="Copy link to AppConfig²"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* CENTER: Learn more + Copyright + Legal Links */}
        <Box sx={{ 
          minWidth: { md: 300 }, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          order: { xs: 3, md: 2 }
        }}>
          <Link
            href="https://www.appconfig.eu"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                textDecoration: 'underline',
                transform: 'translateY(-1px)',
                color: theme.palette.warning.main
              }
            }}
          >
            Learn more about AppConfig²
            <LaunchIcon sx={{ fontSize: '0.95rem' }} />
          </Link>

          <Link
            href="https://oauth.appconfig.app"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.85rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main'
              }
            }}
          >
            OAuth Workshop Companion App
            <LaunchIcon sx={{ fontSize: '0.8rem' }} />
          </Link>
          
          {/* Copyright */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontWeight: 500,
              letterSpacing: '0.01em'
            }}
          >
            © {new Date().getFullYear()} AppConfig². All rights reserved.
          </Typography>

          {/* Legal links - moved here */}
          <Typography variant="body2" color="textSecondary" component="div">
            <Link href="/terms-of-use.html" target="_blank" rel="noopener noreferrer" sx={legalLinkSx}>Terms of Use</Link>
            <Box component="span" sx={{ mx: 1.5, opacity: 0.5 }}>
              •
            </Box>
            <Link href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" sx={legalLinkSx}>Privacy Policy</Link>
            <Box component="span" sx={{ mx: 1.5, opacity: 0.5 }}>
              •
            </Box>
            <ButtonBase
              onClick={() => setCookiePrefsOpen(true)}
              sx={{ ...legalLinkSx, fontSize: '0.875rem', px: 0, py: 0 }}
              aria-label="Manage cookie preferences"
            >
              Manage cookies
            </ButtonBase>
          </Typography>
          {/* Dedication - moved to center below Privacy Policy */}
            <Tooltip title={<><FavoriteIcon sx={{ fontSize: '0.75rem', mr: 0.5 }} /> Stari moj</>} placement="top">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              opacity: 0.6,
              mt: 0,
              mb: 0.5,
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.9 }
            }}>
              <FavoriteIcon sx={{ 
                fontSize: '0.75rem', 
                color: 'text.secondary'
              }} />
            </Box>
          </Tooltip>
        </Box>

        {/* RIGHT: Support links + Version */}
        <Box sx={{ 
          minWidth: { md: 300 }, 
          textAlign: { xs: 'left', md: 'right' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          gap: 1,
          order: { xs: 2, md: 3 }
        }}>
          {/* Support Links */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'flex-end' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Support:
            </Typography>
            <Tooltip title="Submit Support Request">
              <IconButton
                size="small"
                onClick={handleSupportRequest}
                aria-label="Submit Support Request"
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'primary.dark'
                  }
                }}
              >
                <ContactSupportIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Release Notes">
              <IconButton
                size="small"
                component="a"
                href="/appconfig-release-notes.html"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Release Notes"
              >
                <NewReleasesIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Getting Started Guide">
              <IconButton
                size="small"
                component="a"
                href="/getting-started.html"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Getting Started Guide"
              >
                <InfoIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Security Hardening Guide (AppConfig)">
              <IconButton
                size="small"
                component="a"
                href="/security-hardening.html"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Security Hardening Guide (AppConfig)"
              >
                <SecurityIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Documentation">
              <IconButton
                size="small"
                component="a"
                href="https://github.com/toninofilip/appconfig-suite#readme"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Documentation"
              >
                <QuestionAnswerIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send Feedback">
              <IconButton
                size="small"
                onClick={handleFeedback}
                aria-label="Send Feedback"
              >
                <FeedbackIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Version info - aligned to right edge */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.5, alignSelf: { md: 'flex-end' }, mr: { md: 1 } }}>
            <Chip
              label={`v${appVersion}`}
              size="small"
              variant="outlined"
              sx={{ opacity: 0.75, fontWeight: 500, letterSpacing: '0.5px' }}
              aria-label={`Application version ${appVersion}`}
            />
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="AppConfig² link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{ role: 'status', 'aria-live': 'polite' }}
      />

      <CookiePreferencesDialog
        open={cookiePrefsOpen}
        onClose={() => setCookiePrefsOpen(false)}
      />
    </Box>
  );
};

export default Footer;